/**
 * API 配置
 */

// API 基础 URL
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:65182/api/v1';

// API 端点
export const API_ENDPOINTS = {
  // 系统管理模块（对应后端 system_app）
  system_app: {
    // 登录相关
    sendCode: '/system_app/send_code',
    login: '/system_app/login',
    loginByPassword: '/system_app/login_by_password',
    currentUser: '/system_app/current_user',
    // 管理员用户管理
    users: '/system_app/users',
    userDetail: (id: number) => `/system_app/users/${id}`,
    // 角色管理
    roles: '/system_app/roles',
    roleDetail: (id: number) => `/system_app/roles/${id}`,
    // 权限管理
    permissions: '/system_app/permissions',
    generationSettings: '/system_app/generation_settings',
    readerAdvertisementSettings: '/system_app/reader_advertisement_settings',
    // 初始化
    init: '/system_app/init',
  },
  
  // AI管理模块
  ai_management: {
    config: '/ai_management/config',
    test: '/ai_management/test',
    testImage: '/ai_management/test-image',
    polarisImageConfig: '/ai_management/polaris-image/config',
    polarisImageModels: '/ai_management/polaris-image/models',
    polarisImageGenerate: '/ai_management/polaris-image/generate',
    polarisImageGenerations: '/ai_management/polaris-image/generations',
    characterNameLibrary: '/ai_management/character-name-library',
    characterNameDetail: (id: number) => `/ai_management/character-name-library/${id}`,
    characterNameImport: '/ai_management/character-name-library/import',
  },

  // AI费用监控模块
  ai_history: {
    list: '/ai_history',
    stats: '/ai_history/stats',
  },

  // 云存储配置
  storage: {
    providers: '/storage_app/providers',
    providerDetail: (id: number) => `/storage_app/providers/${id}`,
    setDefault: (id: number) => `/storage_app/providers/${id}/default`,
    test: (id: number) => `/storage_app/providers/${id}/test`,
    resources: '/storage_app/resources',
  },

  // 小说资源拉取
  resourcePull: {
    platforms: '/resource_pull/platforms',
    categories: '/resource_pull/categories',
    pull: '/resource_pull/pull',
    tasks: '/resource_pull/tasks',
    cancelTask: (id: number) => `/resource_pull/tasks/${id}/cancel`,
    records: '/resource_pull/records',
    shuhaigeSearch: '/resource_pull/shuhaige/search',
    shuhaigeCrawl: '/resource_pull/shuhaige/crawl',
    uploads: '/resource_pull/uploads',
    uploadTasks: '/resource_pull/upload-tasks',
  },

  initialization: {
    tasks: '/initialization/tasks',
    taskDetail: (id: string) => `/initialization/tasks/${id}`,
    resume: (id: string) => `/initialization/tasks/${id}/resume`,
    pause: (id: string) => `/initialization/tasks/${id}/pause`,
    skipCurrent: (id: string) => `/initialization/tasks/${id}/skip-current`,
  },

  // 数据看板
  dashboard: {
    overview: '/dashboard/overview',
  },

  // 埋点数据
  analytics: {
    overview: '/analytics/overview',
    events: '/analytics/events',
    exportEvents: '/analytics/events/export',
  },

  // 运营管理
  operations: {
    overview: '/operations/overview',
    orders: '/operations/orders',
    refundOrder: (id: string) => `/operations/orders/${id}/refund`,
    refunds: '/operations/refunds',
    reviewRefund: (id: string) => `/operations/refunds/${id}/review`,
    inkAccounts: '/operations/ink/accounts',
    inkRecords: '/operations/ink/records',
    inkTransfers: '/operations/ink/transfers',
    workflows: '/operations/workflows',
    books: '/operations/books',
    exportBooks: '/operations/books/export',
    exportJobs: '/operations/books/export-jobs',
    exportJob: (id: string) => `/operations/books/export-jobs/${id}`,
    exportPart: (id: string, index: number) => `/operations/books/export-jobs/${id}/parts/${index}`,
    importBooks: '/operations/books/import',
    bookChapters: (id: number) => `/operations/books/${id}/chapters`,
    bookChapter: (id: number, chapterId: number) => `/operations/books/${id}/chapters/${chapterId}`,
    regenerateBookCover: (id: number) => `/operations/books/${id}/cover/regenerate`,
    bookStatus: (id: number) => `/operations/books/${id}/status`,
    bookPaywall: (id: number) => `/operations/books/${id}/paywall`,
    bookCategory: (id: number) => `/operations/books/${id}/category`,
    deleteBook: (id: number) => `/operations/books/${id}`,
    libraryRankings: '/operations/library/rankings',
    siteNews: '/operations/library/news',
    bookshelves: '/operations/library/bookshelves',
    libraryCategories: '/operations/library/categories',
    librarySections: '/operations/library/sections',
    libraryAuthors: '/operations/library/authors',
    libraryAuthor: (id: string) => `/operations/library/authors/${id}`,
    batchDeleteAuthors: '/operations/library/authors/batch-delete',
    generateAuthorAvatar: '/operations/library/authors/generate-avatar',
    campaigns: '/operations/campaigns',
  },

  // 内容中台配置
  contentStudio: {
    config: '/content_studio/config',
  },

  // 北极星数据上报
  dataReporting: {
    overview: '/data_reporting/overview',
    config: '/data_reporting/config',
    records: '/data_reporting/records',
  },

  // 鹿鸣数据上报
  lumingReporting: {
    overview: '/luming_reporting/overview',
    tasks: '/luming_reporting/tasks',
    records: '/luming_reporting/records',
  },

  // 支付中心配置与联调
  paymentManagement: {
    config: '/payment_management/config',
    testChannels: '/payment_management/test/channels',
    testOrders: '/payment_management/test/orders',
    testCallbacks: '/payment_management/test/callbacks',
    records: '/payment_management/test/records',
  },

};

// 默认请求头
export const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
};

/**
 * 构建完整的 API URL
 */
export function buildApiUrl(endpoint: string, params?: Record<string, any>): string {
  let url = `${API_BASE_URL}${endpoint}`;
  
  if (params) {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, String(value));
      }
    });
    const queryString = queryParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }
  
  return url;
}
